import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { formatDate, formatNumber } from "../utils/format";
import ErrorModal from "../components/ErrorModal";
import { usePermission } from "../hooks/usePermission";

export default function StockCountList() {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("stock_counts.create");
  const canEdit = hasPermission("stock_counts.edit");
  const canDelete = hasPermission("stock_counts.delete");
  const [stockCounts, setStockCounts] = useState([]);
  const [unauthorized, setUnauthorized] = useState(false);
  const [sortField, setSortField] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingStockCount, setDeletingStockCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStockCounts, setLoadingStockCounts] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupingLoading, setGroupingLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [overviewData, setOverviewData] = useState(null);
  const [overviewSortField, setOverviewSortField] = useState("brand_name");
  const [overviewSortOrder, setOverviewSortOrder] = useState("asc");
  const [overviewBrandFilter, setOverviewBrandFilter] = useState("");
  const [overviewSearchFilter, setOverviewSearchFilter] = useState("");
  const [overviewSortType, setOverviewSortType] = useState(null); // 'absolute' or 'delta'
  const [overviewSortStockCountId, setOverviewSortStockCountId] = useState(null);

  useEffect(() => {
    fetchStockCounts();
    fetchOverviewData();
  }, [sortField, sortOrder]);

  const fetchStockCounts = () => {
    setLoadingStockCounts(true);
    const params = new URLSearchParams();
    params.append("sort", sortField);
    params.append("order", sortOrder);

    apiFetch(`/stock-counts?${params.toString()}`)
      .then(setStockCounts)
      .catch((err) => {
        console.error("Error fetching stock counts:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoadingStockCounts(false));
  };

  const fetchOverviewData = () => {
    apiFetch("/stock-counts/overview/last-five")
      .then(setOverviewData)
      .catch((err) => {
        console.error("Error fetching overview data:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleAdd = () => {
    navigate("/stock-counts/new");
  };

  const handleEdit = (stockCount) => {
    navigate(`/stock-counts/${stockCount.id}`);
  };

  const handleDelete = (stockCount) => {
    setDeletingStockCount(stockCount);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!deletingStockCount) return;

    setLoading(true);
    apiFetch(`/stock-counts/${deletingStockCount.id}`, { method: "DELETE" })
      .then(() => {
        setShowDeleteModal(false);
        setDeletingStockCount(null);
        fetchStockCounts();
        fetchOverviewData();
      })
      .catch((err) => {
        console.error("Fout bij verwijderen stockCount:", err);
        setShowDeleteModal(false);
        setDeletingStockCount(null);
        // Show user-friendly error message
        if (err.message && err.message.includes("part of a group")) {
          setErrorMessage("Deze telling maakt deel uit van een gegroepeerde telling en kan daarom niet verwijderd worden. Verwijder eerst de gegroepeerde telling, of haal deze telling uit de groep.");
        } else if (err.message.includes("403")) {
          setUnauthorized(true);
        } else {
          setErrorMessage(err.message || "Er is een fout opgetreden bij het verwijderen van de telling.");
        }
      })
      .finally(() => setLoading(false));
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "↕";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleGroupSelected = () => {
    if (selectedIds.length < 2) {
      setErrorModal({ show: true, message: "Selecteer minimaal 2 voorraadtellingen om te groeperen." });
      return;
    }

    // Check if any selected count is already grouped
    const selectedCounts = stockCounts.filter((sc) => selectedIds.includes(sc.id));
    const alreadyGrouped = selectedCounts.filter((sc) => sc.is_grouped);
    if (alreadyGrouped.length > 0) {
      setErrorModal({ show: true, message: `De volgende tellingen zijn al gegroepeerd en kunnen niet opnieuw gegroepeerd worden: ${alreadyGrouped.map(sc => sc.name).join(", ")}` });
      return;
    }

    setGroupingLoading(true);
    apiFetch("/stock-counts/group", {
      method: "POST",
      body: JSON.stringify({ stock_count_ids: selectedIds }),
    })
      .then(() => {
        setSelectedIds([]);
        fetchStockCounts();
        fetchOverviewData();
      })
      .catch((err) => {
        console.error("Fout bij groeperen tellingen:", err);
        setErrorModal({ show: true, message: "Er is een fout opgetreden bij het groeperen van de tellingen." });
      })
      .finally(() => setGroupingLoading(false));
  };

  // Get non-grouped stock counts for selection (can't select grouped counts)
  const selectableStockCounts = stockCounts.filter((sc) => !sc.is_grouped);

  const handleOverviewSort = (field) => {
    // Clear stock count sorting when sorting by brand/product
    setOverviewSortType(null);
    setOverviewSortStockCountId(null);

    if (overviewSortField === field) {
      setOverviewSortOrder(overviewSortOrder === "asc" ? "desc" : "asc");
    } else {
      setOverviewSortField(field);
      setOverviewSortOrder("asc");
    }
  };

  const handleOverviewStockCountSort = (stockCountId, sortType) => {
    // Clear brand/product sorting when sorting by stock count
    setOverviewSortField(null);

    // Toggle order if clicking same column/type, otherwise set to desc
    if (overviewSortStockCountId === stockCountId && overviewSortType === sortType) {
      setOverviewSortOrder(overviewSortOrder === "asc" ? "desc" : "asc");
    } else {
      setOverviewSortStockCountId(stockCountId);
      setOverviewSortType(sortType);
      setOverviewSortOrder("desc"); // Default to descending for quantities
    }
  };

  const getOverviewSortIcon = (field) => {
    if (overviewSortField !== field) return "↕";
    return overviewSortOrder === "asc" ? "↑" : "↓";
  };

  const getStockCountSortIcon = (stockCountId, sortType) => {
    if (overviewSortStockCountId !== stockCountId || overviewSortType !== sortType) {
      return "↕";
    }
    return overviewSortOrder === "asc" ? "↑" : "↓";
  };

  // Pagination logic
  const totalPages = Math.ceil(stockCounts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStockCounts = stockCounts.slice(startIndex, endIndex);

  // Filter and sort overview data
  const getFilteredAndSortedOverview = () => {
    if (!overviewData || !overviewData.products) return [];

    let filtered = overviewData.products;

    // Apply brand filter
    if (overviewBrandFilter) {
      filtered = filtered.filter(p => String(p.brand_id) === String(overviewBrandFilter));
    }

    // Apply search filter
    if (overviewSearchFilter) {
      const search = overviewSearchFilter.toLowerCase();
      filtered = filtered.filter(p =>
        p.product_name.toLowerCase().includes(search) ||
        p.brand_name.toLowerCase().includes(search)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let valA, valB;

      // Sort by stock count quantity (absolute or delta)
      if (overviewSortStockCountId && overviewSortType) {
        if (overviewSortType === 'absolute') {
          // Sort by absolute quantity value
          valA = a.quantities[overviewSortStockCountId] || 0;
          valB = b.quantities[overviewSortStockCountId] || 0;
        } else if (overviewSortType === 'delta') {
          // Sort by difference with previous stock count
          const stockCountIndex = overviewData.stock_counts.findIndex(sc => sc.id === overviewSortStockCountId);
          if (stockCountIndex < overviewData.stock_counts.length - 1) {
            const currentSc = overviewData.stock_counts[stockCountIndex];
            const previousSc = overviewData.stock_counts[stockCountIndex + 1];

            const aCurrentQty = a.quantities[currentSc.id] || 0;
            const aPreviousQty = a.quantities[previousSc.id] || 0;
            valA = aCurrentQty - aPreviousQty;

            const bCurrentQty = b.quantities[currentSc.id] || 0;
            const bPreviousQty = b.quantities[previousSc.id] || 0;
            valB = bCurrentQty - bPreviousQty;
          } else {
            // Last column has no delta
            valA = 0;
            valB = 0;
          }
        }
      } else if (overviewSortField === "brand_name") {
        valA = a.brand_name;
        valB = b.brand_name;
      } else if (overviewSortField === "product_name") {
        valA = a.product_name;
        valB = b.product_name;
      } else {
        return 0;
      }

      if (typeof valA === 'string' && typeof valB === 'string') {
        if (valA < valB) return overviewSortOrder === "asc" ? -1 : 1;
        if (valA > valB) return overviewSortOrder === "asc" ? 1 : -1;
      } else {
        // Numeric comparison
        if (valA < valB) return overviewSortOrder === "asc" ? -1 : 1;
        if (valA > valB) return overviewSortOrder === "asc" ? 1 : -1;
      }

      return 0;
    });

    return filtered;
  };

  const filteredOverviewProducts = getFilteredAndSortedOverview();

  // Get unique brands for filter
  const overviewBrands = overviewData && overviewData.products
    ? [...new Map(overviewData.products.map(p => [p.brand_id, { id: p.brand_id, name: p.brand_name }])).values()]
    : [];

  if (unauthorized) return <Navigate to="/unauthorized" replace />;

  return (
    <div className="p-4 sm:p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl sm:text-2xl font-bold">Voorraadtellingen</h1>
        <div className="flex gap-2">
          {canEdit && selectedIds.length >= 2 && (
            <button
              onClick={handleGroupSelected}
              disabled={groupingLoading}
              className="hidden sm:block bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400"
            >
              {groupingLoading ? "Bezig..." : `Tellingen groeperen (${selectedIds.length})`}
            </button>
          )}
          {canCreate && (
            <>
              <button
                onClick={() => navigate("/stock-counts/scan")}
                className="sm:hidden bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 flex items-center"
              >
                {/* QR icon with plus badge */}
                <div className="relative">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                  {/* Plus badge in top-right corner */}
                  <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-white rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                </div>
              </button>
              <button
                onClick={handleAdd}
                className="hidden sm:block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                + Nieuwe Voorraadtelling
              </button>
            </>
          )}
        </div>
      </div>

      {/* Loading Indicator */}
      {loadingStockCounts && (
        <div className="flex justify-center items-center py-12 bg-white shadow rounded-lg mb-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Voorraadtellingen laden...</span>
        </div>
      )}

      {/* Voorraadtellingen - Mobile Cards */}
      {!loadingStockCounts && (
        <div className="sm:hidden space-y-3">
          {stockCounts.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-4 text-center text-gray-500">
              Geen voorraadtellingen gevonden
            </div>
          ) : (
            paginatedStockCounts.map((st) => (
              <div
                key={st.id}
                className={`bg-white shadow rounded-lg p-4 ${st.is_grouped ? 'border-l-4 border-purple-500' : ''}`}
                onClick={() => canEdit && handleEdit(st)}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{st.name}</h3>
                      {st.is_grouped && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                          Gegroepeerd
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{formatDate(st.date)}</p>
                  </div>
                  {canDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(st);
                      }}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{st.item_count} producten</span>
                  <span className="font-medium text-gray-900">€{formatNumber(st.total_value)}</span>
                </div>
                {st.notes && (
                  <p className="text-sm text-gray-500 mt-2 truncate">{st.notes}</p>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Voorraadtellingen Table - Desktop */}
      {!loadingStockCounts && (
        <div className="hidden sm:block bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider w-10">
                  {/* Checkbox column */}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort("date")}
                >
                  Datum {getSortIcon("date")}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort("name")}
                >
                  Naam {getSortIcon("name")}
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort("item_count")}
                >
                  Aantal Producten {getSortIcon("item_count")}
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort("total_value")}
                >
                  Totale Waarde {getSortIcon("total_value")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Opmerkingen
                </th>
                {(canEdit || canDelete) && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Acties
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stockCounts.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  Geen voorraadtellingen gevonden
                </td>
              </tr>
            ) : (
              paginatedStockCounts.map((st) => (
                <tr key={st.id} className={`hover:bg-gray-50 ${st.is_grouped ? 'bg-purple-50' : ''}`}>
                  <td className="px-3 py-4 text-center">
                    {!st.is_grouped && (
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(st.id)}
                        onChange={() => toggleSelection(st.id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {formatDate(st.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      {st.name}
                      {st.is_grouped && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                          Gegroepeerd
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                    {st.item_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                    €{formatNumber(st.total_value)}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {st.notes || "-"}
                  </td>
                  {(canEdit || canDelete) && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {canEdit && (
                        <button
                          onClick={() => handleEdit(st)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Bewerken
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(st)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Verwijderen
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Vorige
          </button>
          <span className="text-gray-700">
            Pagina {currentPage} van {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Volgende →
          </button>
        </div>
      )}

      {/* Overview of Last 5 Stock Counts - Hidden on mobile */}
      {overviewData && overviewData.stock_counts && overviewData.stock_counts.length > 0 && (
        <div className="hidden sm:block mt-8">
          <h2 className="text-xl font-bold mb-4">Overzicht van de laatste 5 tellingen</h2>

          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <select
              value={overviewBrandFilter}
              onChange={(e) => setOverviewBrandFilter(e.target.value)}
              className="px-3 py-2 border rounded"
            >
              <option value="">Alle merken</option>
              {overviewBrands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Zoek product of merk..."
              value={overviewSearchFilter}
              onChange={(e) => setOverviewSearchFilter(e.target.value)}
              className="px-3 py-2 border rounded flex-1"
            />
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                      onClick={() => handleOverviewSort("brand_name")}
                    >
                      Merk {getOverviewSortIcon("brand_name")}
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                      onClick={() => handleOverviewSort("product_name")}
                    >
                      Product {getOverviewSortIcon("product_name")}
                    </th>
                    {overviewData.stock_counts.map((sc, index) => {
                      const isLastColumn = index === overviewData.stock_counts.length - 1;
                      return (
                        <th
                          key={sc.id}
                          className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
                        >
                          <div className="flex items-center justify-end gap-2">
                            <span>{formatDate(sc.date)}</span>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleOverviewStockCountSort(sc.id, 'absolute')}
                                className="hover:bg-gray-300 px-1 rounded"
                                title="Sorteer op absolute waarde"
                              >
                                {getStockCountSortIcon(sc.id, 'absolute')}
                              </button>
                              {!isLastColumn && (
                                <button
                                  onClick={() => handleOverviewStockCountSort(sc.id, 'delta')}
                                  className="hover:bg-gray-300 px-1 rounded"
                                  title="Sorteer op verschil"
                                >
                                  {getStockCountSortIcon(sc.id, 'delta')}
                                </button>
                              )}
                            </div>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOverviewProducts.length === 0 ? (
                    <tr>
                      <td colSpan={2 + overviewData.stock_counts.length} className="px-6 py-4 text-center text-gray-500">
                        Geen producten gevonden
                      </td>
                    </tr>
                  ) : (
                    filteredOverviewProducts.map((product) => {
                      // Calculate differences between stock counts
                      const getDifference = (currentIndex) => {
                        if (currentIndex === overviewData.stock_counts.length - 1) {
                          // Rightmost column (oldest), no difference to show
                          return null;
                        }
                        const currentSc = overviewData.stock_counts[currentIndex];
                        const previousSc = overviewData.stock_counts[currentIndex + 1];
                        const currentQty = product.quantities[currentSc.id] || 0;
                        const previousQty = product.quantities[previousSc.id] || 0;
                        return currentQty - previousQty;
                      };

                      return (
                        <tr
                          key={product.product_id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => navigate(`/products/${product.product_id}`)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                            {product.brand_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                            {product.product_name}
                          </td>
                          {overviewData.stock_counts.map((sc, index) => {
                            const quantity = product.quantities[sc.id];
                            const difference = getDifference(index);

                            return (
                              <td key={sc.id} className="px-6 py-4 whitespace-nowrap text-right text-gray-900">
                                {quantity ? formatNumber(quantity, 0) : '-'}
                                {difference !== null && difference !== 0 && (
                                  <span className={`ml-2 text-sm ${difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    ({difference > 0 ? '+' : ''}{formatNumber(difference, 0)})
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Verwijderen niet mogelijk</h2>
            </div>
            <p className="mb-6 text-gray-700">{errorMessage}</p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setErrorMessage(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingStockCount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Voorraadtelling Verwijderen</h2>
            <p className="mb-6 text-gray-700">
              Weet je zeker dat je de stockCount "<strong>{deletingStockCount.name}</strong>" wilt verwijderen?
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingStockCount(null);
                }}
                className="px-4 py-2 border rounded hover:bg-gray-100"
                disabled={loading}
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? "Bezig..." : "Verwijderen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal
        show={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: "" })}
      />
    </div>
  );
}
